"use client";

import { useId, useState } from "react";
import { Plus } from "lucide-react";

import { readInternalSource } from "@/lib/analytics/internal-source";
import { cn } from "@/lib/utils";
import { useAddToCart } from "@/ui/components/cart/use-add-to-cart";

export interface BacQtyLabel {
	qty: number;
	/** Saleor variant id for this pack size. */
	variantId: string;
	/** Pack total (e.g., "16,99 €" for 2-pack with discount baked in). */
	totalLabel: string;
	/** Per-unit derived from this variant's actual price (e.g., "8,50 €" for 2-pack). */
	perUnitLabel: string;
	/** Optional savings vs. unit×n (e.g., "-0,99 €"). */
	savingsLabel?: string;
}

interface BacCrossSellQtyPickerProps {
	channel: string;
	addLabel: string;
	/**
	 * Accessible name of the pack-size radiogroup, e.g. "Stück" / "pcs" — the same
	 * label the main buy-box picker uses. Must be distinct from `addLabel` so
	 * screen readers don't announce the selector and the add button identically.
	 */
	qtyGroupLabel: string;
	/** Optional plural for localized vial labels. */
	pluralQtyLabel?: string;
	/** Visible description of the pack contents, e.g. "10 ml vials". */
	packLabel: string;
	/** Label for the selected pack's total price. */
	totalLabel: string;
	/** Pre-resolved (reference-currency-aware) labels per qty, computed server-side. */
	qtyLabels: BacQtyLabel[];
	/** Per-unit suffix label, e.g. "/Stk" / "/ea" */
	perUnitSuffix: string;
	/** BAC product slug — also the `product_slug` of the resulting add-to-cart event. */
	productSlug: string;
	/** Slug of the PDP this card sits on — the `internal_source_detail` of the add. */
	hostProductSlug: string;
	// locale reserved for future use (e.g. redirect)
	locale: string;
}

export function BacCrossSellQtyPicker({
	channel,
	addLabel,
	qtyGroupLabel,
	pluralQtyLabel,
	packLabel,
	totalLabel,
	qtyLabels,
	perUnitSuffix,
	productSlug,
	hostProductSlug,
}: BacCrossSellQtyPickerProps) {
	const packGroupId = useId();
	const [qty, setQty] = useState(qtyLabels[0]?.qty ?? 1);
	const [isPending, setIsPending] = useState(false);
	// Shared runner: opens the drawer with a transient line right away and owns the
	// failure toast + retry (which used to be hand-rolled here).
	const addToCart = useAddToCart();

	const active = qtyLabels.find((q) => q.qty === qty) ?? qtyLabels[0];

	const handleAdd = async () => {
		if (!active || isPending) return;
		setIsPending(true);
		// Same inheritance as the buybox: this card is part of the product page, so an add
		// here belongs to whatever link opened it (the search/calculator click handoff, or
		// `?src=` on older links), falling back to the unreferred product page itself.
		const referred =
			typeof window !== "undefined"
				? readInternalSource(window.location.search, window.location.hash, window.location.pathname)
				: {};
		await addToCart({
			channel,
			variantId: active.variantId,
			quantity: 1,
			productSlug,
			productName: "BAC-Water 10 ml",
			variantName: `${active.qty} ${active.qty === 1 || !pluralQtyLabel ? qtyGroupLabel : pluralQtyLabel}`,
			internalSource: referred.internal_source ?? "pdp",
			internalSourceDetail: referred.internal_source ? referred.internal_source_detail : hostProductSlug,
			addSurface: "bac_cross_sell",
		});
		setIsPending(false);
	};

	return (
		<>
			<p id={`${packGroupId}-label`} className="mb-2 text-xs text-muted-foreground">
				{packLabel}
			</p>
			<div
				role="radiogroup"
				aria-label={qtyGroupLabel}
				aria-describedby={`${packGroupId}-label`}
				className={cn(
					"grid gap-2",
					qtyLabels.length === 1 ? "grid-cols-1" : qtyLabels.length === 2 ? "grid-cols-2" : "grid-cols-3",
				)}
			>
				{qtyLabels.map(({ qty: n, totalLabel: packTotalLabel }) => (
					<label key={n} className="relative min-w-0 cursor-pointer">
						<input
							type="radio"
							name={packGroupId}
							value={n}
							checked={active?.qty === n}
							onChange={() => setQty(n)}
							aria-label={`${n} ${n === 1 || !pluralQtyLabel ? qtyGroupLabel : pluralQtyLabel}, ${packTotalLabel}`}
							className="peer sr-only"
						/>
						<span className="flex min-h-11 min-w-0 flex-wrap items-baseline justify-center gap-x-1 rounded-lg border border-border bg-card px-2 py-3 text-center text-foreground transition-colors hover:bg-muted peer-checked:border-primary peer-checked:bg-accent-light peer-checked:text-primary peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
							<span className="text-sm font-bold">{n}</span>
							<span className="min-w-0 text-xs [overflow-wrap:anywhere]">
								{n === 1 || !pluralQtyLabel ? qtyGroupLabel : pluralQtyLabel}
							</span>
						</span>
					</label>
				))}
			</div>
			<div className="mt-3 flex flex-wrap items-center gap-3">
				<div className="min-w-0 flex-1 basis-32" aria-live="polite" aria-atomic="true">
					<p className="text-[11px] text-muted-foreground">{totalLabel}</p>
					<p className="text-xl font-bold leading-tight text-foreground [overflow-wrap:anywhere]">
						{active?.totalLabel ?? ""}
					</p>
					{active && active.qty > 1 ? (
						<p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs">
							<span className="text-muted-foreground [overflow-wrap:anywhere]">
								{active.perUnitLabel}
								{perUnitSuffix}
							</span>
							{active.savingsLabel ? (
								<span className="font-medium text-success [overflow-wrap:anywhere]">
									{active.savingsLabel}
								</span>
							) : null}
						</p>
					) : null}
				</div>
				<button
					type="button"
					onClick={() => void handleAdd()}
					disabled={isPending || !active}
					className="ml-auto inline-flex min-h-11 min-w-0 max-w-full items-center justify-center gap-1.5 rounded-lg border border-primary bg-card px-3 py-2.5 text-center text-sm font-semibold text-primary transition-colors hover:bg-accent-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
				>
					<Plus className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
					<span className="min-w-0 [overflow-wrap:anywhere]">{addLabel}</span>
				</button>
			</div>
		</>
	);
}
